import { Type, CircleDot, CheckSquare, ChevronDown, Plus, Trash2, ArrowUp, ArrowDown, CheckSquare2, CalendarDays, Globe, Phone, UserRound } from "lucide-react";
import { LegalEditor } from "@/components/LegalEditor";

const SESSION_PRESET = ["October 15th at 3 PM AEST", "October 15th at 10 AM BST", "October 15th at 9 AM PT / 12 PM ET"];

const TYPES = [
  ["text", "Text", Type],
  ["choice", "Pick one", CircleDot],
  ["checkboxes", "Pick several", CheckSquare],
  ["dropdown", "Dropdown", ChevronDown],
  ["country", "Country", Globe],
  ["phone", "Phone", Phone],
];
const DEFAULT_LABEL = { country: "Country", phone: "Phone number" };
const HAS_OPTIONS = ["choice", "checkboxes", "dropdown"];

const uid = () => `f${Math.random().toString(36).slice(2, 9)}`;
const inputCls = "w-full h-9 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none";
const mini = "h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-white hover:bg-white/5 transition-colors";

export const FormBuilder = ({ form, setForm }) => {
  const setFields = (fields) => setForm({ ...form, fields });
  const setOptins = (optins) => setForm({ ...form, optins });
  const updF = (i, patch) => setFields(form.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const updO = (i, patch) => setOptins(form.optins.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  const legal = form.legal || { parties: [], require_agree: false };
  const parties = legal.parties || [];
  const removeParty = (pi) => setForm({
    ...form,
    legal: { ...legal, parties: parties.filter((_, j) => j !== pi) },
    optins: form.optins.map((o) => ({ ...o, parties: (o.parties || []).filter((x) => x !== pi).map((x) => (x > pi ? x - 1 : x)) })),
  });
  const move = (arr, setter, i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    const a = [...arr];
    [a[i], a[j]] = [a[j], a[i]];
    setter(a);
  };

  return (
    <div data-testid="form-builder" className="space-y-8">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold">Questions</h3>
          <div className="flex gap-1.5 flex-wrap justify-end">
            <button type="button" data-testid="form-add-preset-sessions" title="Add webinar session picker"
              onClick={() => setFields([{ id: uid(), type: "dropdown", label: "Select a Date", placeholder: "Select a Date", required: true, options: SESSION_PRESET }, ...form.fields])}
              className="h-8 px-2.5 rounded-md border border-dashed border-indigo-500/60 bg-indigo-500/10 hover:bg-indigo-500/20 text-xs text-indigo-200 flex items-center gap-1.5 transition-colors">
              <CalendarDays className="w-3.5 h-3.5" /> Webinar sessions
            </button>
            <button type="button" data-testid="form-add-preset-name" title="Add First name + Last name on one row"
              onClick={() => setFields([...form.fields,
                { id: uid(), type: "text", label: "First name", placeholder: "", required: true, options: [], width: "half" },
                { id: uid(), type: "text", label: "Last name", placeholder: "", required: true, options: [], width: "half" }])}
              className="h-8 px-2.5 rounded-md border border-dashed border-indigo-500/60 bg-indigo-500/10 hover:bg-indigo-500/20 text-xs text-indigo-200 flex items-center gap-1.5 transition-colors">
              <UserRound className="w-3.5 h-3.5" /> First + Last name
            </button>
            {TYPES.map(([t, label, Icon]) => (
              <button key={t} type="button" data-testid={`form-add-field-${t}`} title={`Add ${label}`}
                onClick={() => setFields([...form.fields, { id: uid(), type: t, label: DEFAULT_LABEL[t] || "", placeholder: "", required: false, options: HAS_OPTIONS.includes(t) ? ["Option 1", "Option 2"] : [] }])}
                className="h-8 px-2.5 rounded-md border border-dashed border-[#2f3a50] hover:border-indigo-500 text-xs text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors">
                <Plus className="w-3 h-3" /> <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>
        {form.fields.length === 0 && <p className="text-xs text-slate-500">No questions yet — the form collects email only.</p>}
        <div className="space-y-3">
          {form.fields.map((f, i) => (
            <div key={f.id} data-testid={`form-field-${f.id}`} className="rounded-md border border-[#242C3D] bg-[#0B0F17]/60 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-indigo-400 w-24 shrink-0">{TYPES.find(([t]) => t === f.type)?.[1]}</span>
                <input data-testid={`form-field-label-${f.id}`} value={f.label} onChange={(e) => updF(i, { label: e.target.value })} placeholder="Question label, e.g. Company size" className={inputCls} />
                <button type="button" onClick={() => move(form.fields, setFields, i, -1)} className={mini} title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => move(form.fields, setFields, i, 1)} className={mini} title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" data-testid={`form-field-delete-${f.id}`} onClick={() => setFields(form.fields.filter((_, j) => j !== i))} className={`${mini} hover:!text-rose-400`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                {f.type === "country" && <span className="text-xs text-slate-500 flex-1">Dropdown of all countries.</span>}
                {(f.type === "text" || f.type === "phone") && <input value={f.placeholder} onChange={(e) => updF(i, { placeholder: e.target.value })} placeholder={f.type === "phone" ? "Number placeholder (optional) — visitors pick a country code" : "Placeholder (optional)"} className={`${inputCls} flex-1 min-w-40`} />}
                {HAS_OPTIONS.includes(f.type) && (
                  <textarea data-testid={`form-field-options-${f.id}`} rows={2} value={f.options.join("\n")} onChange={(e) => updF(i, { options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    placeholder="One option per line" className={`${inputCls} h-auto py-2 flex-1 min-w-40 font-mono text-xs resize-none`} />
                )}
                <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer shrink-0" title="Two half-width fields in a row sit side by side">
                  <input type="checkbox" data-testid={`form-field-half-${f.id}`} checked={f.width === "half"} onChange={(e) => updF(i, { width: e.target.checked ? "half" : "full" })} className="accent-indigo-500" /> Half width
                </label>
                <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer shrink-0">
                  <input type="checkbox" data-testid={`form-field-required-${f.id}`} checked={f.required} onChange={(e) => updF(i, { required: e.target.checked })} className="accent-indigo-500" /> Required
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold">Opt-in checkboxes</h3>
          <button type="button" data-testid="form-add-optin" onClick={() => setOptins([...form.optins, { id: uid(), label: "", required: false }])}
            className="h-8 px-2.5 rounded-md border border-dashed border-[#2f3a50] hover:border-indigo-500 text-xs text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors">
            <Plus className="w-3 h-3" /> <CheckSquare2 className="w-3.5 h-3.5" /> Consent
          </button>
        </div>
        {form.optins.length === 0 && <p className="text-xs text-slate-500">No consent checkboxes — e.g. “Send me product updates” or “I accept the terms”.</p>}
        <div className="space-y-2">
          {form.optins.map((o, i) => (
            <div key={o.id} data-testid={`form-optin-${o.id}`} className="rounded-md border border-[#242C3D] bg-[#0B0F17]/60 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input data-testid={`form-optin-label-${o.id}`} value={o.label} onChange={(e) => updO(i, { label: e.target.value })} placeholder="Consent text, e.g. I agree to receive marketing emails" className={inputCls} />
              <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer shrink-0">
                <input type="checkbox" data-testid={`form-optin-required-${o.id}`} checked={o.required} onChange={(e) => updO(i, { required: e.target.checked })} className="accent-indigo-500" /> Required
              </label>
              <button type="button" onClick={() => move(form.optins, setOptins, i, -1)} className={mini}><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => move(form.optins, setOptins, i, 1)} className={mini}><ArrowDown className="w-3.5 h-3.5" /></button>
              <button type="button" data-testid={`form-optin-delete-${o.id}`} onClick={() => setOptins(form.optins.filter((_, j) => j !== i))} className={`${mini} hover:!text-rose-400`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            {parties.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                <span className="text-slate-500">Link legal parties:</span>
                {parties.map((p, pi) => (
                  <label key={pi} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" data-testid={`form-optin-party-${o.id}-${pi}`} className="accent-indigo-500"
                      checked={(o.parties || []).includes(pi)}
                      onChange={(e) => updO(i, { parties: e.target.checked ? [...(o.parties || []), pi].sort() : (o.parties || []).filter((x) => x !== pi) })} />
                    {p.name || `Party ${pi + 1}`}
                  </label>
                ))}
              </div>
            )}
            </div>
          ))}
        </div>
      </div>

      <LegalEditor legal={legal} setLegal={(l) => setForm({ ...form, legal: l })} onRemove={removeParty} />
    </div>
  );
};
